import axiosInstance from '../axiosInstance';
import {
  Member,
  CreateMemberRequest,
  UpdateMemberRequest,
  MemberFilters,
  MembersResponse,
  ImportMembersRequest,
  ImportMembersResponse,
  MemberTransaction,
  UpdateTransactionRequest
} from './types';

const API_BASE_URL = '/api/members';

export const membersApi = {
  // Fetch members with optional filtering and pagination
  fetchMembers: async (filters?: MemberFilters): Promise<{
    data: Member[];
    page: number;
    perPage: number;
    total: number;
  }> => {
    try {
      const params = new URLSearchParams();
      
      if (filters?.search) params.append('search', filters.search);
      if (filters?.accountStatus) params.append('accountStatus', filters.accountStatus);
      if (filters?.state) params.append('state', filters.state);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.perPage) params.append('perPage', filters.perPage.toString());

      // Temporary mock data until backend is ready
      const mockMembers: Member[] = [
        {
          id: '1',
          intacctId: 'ACC-001',
          name: 'Acme Corporation',
          companyName: 'Acme Corporation',
          state: 'CA',
          country: 'USA',
          accountStatus: 'active',
          product: 'Premium',
          subscriptionStatus: 'active',
          frequency: 'monthly',
          startDate: '2023-01-15',
          endDate: null,
          scheduledInvoice: '2024-02-01',
          paymentCollection: 'auto',
          paymentSource: 'credit_card',
          paymentBrand: 'Visa',
          paymentLast4: '4242',
          paymentLabel: 'Corporate Card',
          softwareId: 'SW-001',
          pendingAmount: 15000,
          approvedAmount: 0,
          paidAmount: 45000,
          createdAt: '2023-01-15T10:00:00Z',
          updatedAt: '2024-01-15T10:00:00Z',
        },
        {
          id: '2',
          intacctId: 'ACC-002',
          name: 'TechStart Solutions',
          companyName: 'TechStart Solutions',
          state: 'NY',
          country: 'USA',
          accountStatus: 'active',
          product: 'Standard',
          subscriptionStatus: 'active',
          frequency: 'quarterly',
          startDate: '2023-03-01',
          endDate: null,
          scheduledInvoice: null,
          paymentCollection: 'manual',
          paymentSource: 'ach',
          paymentBrand: null,
          paymentLast4: null,
          paymentLabel: null,
          softwareId: 'SW-002',
          pendingAmount: 0,
          approvedAmount: 0,
          paidAmount: 32000,
          createdAt: '2023-03-01T10:00:00Z',
          updatedAt: '2024-01-10T10:00:00Z',
        },
        {
          id: '3',
          intacctId: 'ACC-003',
          name: 'Global Industries Inc',
          companyName: 'Global Industries Inc',
          state: 'TX',
          country: 'USA',
          accountStatus: 'active',
          product: 'Enterprise',
          subscriptionStatus: 'active',
          frequency: 'annual',
          startDate: '2022-06-01',
          endDate: null,
          scheduledInvoice: '2024-06-01',
          paymentCollection: 'auto',
          paymentSource: 'credit_card',
          paymentBrand: 'MasterCard',
          paymentLast4: '5555',
          paymentLabel: 'Finance Dept',
          softwareId: null,
          pendingAmount: 8500,
          approvedAmount: 12000,
          paidAmount: 120000,
          createdAt: '2022-06-01T10:00:00Z',
          updatedAt: '2024-01-12T10:00:00Z',
        },
      ];

      // Apply filters
      let filteredMembers = [...mockMembers];
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        filteredMembers = filteredMembers.filter(m => 
          m.companyName.toLowerCase().includes(searchLower) ||
          m.intacctId.toLowerCase().includes(searchLower)
        );
      }
      if (filters?.accountStatus && filters.accountStatus !== 'all') {
        filteredMembers = filteredMembers.filter(m => m.accountStatus === filters.accountStatus);
      }

      const page = filters?.page || 1;
      const limit = filters?.perPage || 25;
      const start = (page - 1) * limit;
      const paginatedMembers = filteredMembers.slice(start, start + limit);

      return {
        data: paginatedMembers,
        total: filteredMembers.length,
        page,
        perPage: limit,
      };

      // Uncomment when backend is ready
      // const response = await axiosInstance.get(`${API_BASE_URL}?${params.toString()}`);
      // return {
      //   data: response.data.data,
      //   page: response.data.page,
      //   perPage: response.data.perPage,
      //   total: response.data.total,
      // };
    } catch (error) {
      console.error('Error fetching members:', error);
      throw new Error('Failed to fetch members');
    }
  },

  // Fetch single member by ID
  fetchMember: async (id: string): Promise<Member> => {
    try {
      const response = await axiosInstance.get<{ data: Member }>(`${API_BASE_URL}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching member:', error);
      throw new Error('Failed to fetch member details');
    }
  },

  // Create new member
  createMember: async (data: CreateMemberRequest): Promise<Member> => {
    try {
      const response = await axiosInstance.post<{ data: Member }>(API_BASE_URL, data);
      return response.data.data;
    } catch (error) {
      console.error('Error creating member:', error);
      throw new Error('Failed to create member');
    }
  },

  // Update existing member
  updateMember: async (id: string, data: Partial<UpdateMemberRequest>): Promise<Member> => {
    try {
      const response = await axiosInstance.put<{ data: Member }>(`${API_BASE_URL}/${id}`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error updating member:', error);
      throw new Error('Failed to update member');
    }
  },

  // Delete member
  deleteMember: async (id: string): Promise<void> => {
    try {
      await axiosInstance.delete(`${API_BASE_URL}/${id}`);
    } catch (error) {
      console.error('Error deleting member:', error);
      throw new Error('Failed to delete member');
    }
  },

  // Search members
  searchMembers: async (searchTerm: string): Promise<Member[]> => {
    try {
      const response = await axiosInstance.get<{ data: Member[] }>(`${API_BASE_URL}/search?q=${encodeURIComponent(searchTerm)}`);
      return response.data.data;
    } catch (error) {
      console.error('Error searching members:', error);
      throw new Error('Failed to search members');
    }
  },

  // Import members from CSV
  importMembers: async (data: ImportMembersRequest): Promise<ImportMembersResponse> => {
    try {
      const response = await axiosInstance.post<ImportMembersResponse>(`${API_BASE_URL}/import`, data);
      return response.data;
    } catch (error) {
      console.error('Error importing members:', error);
      throw new Error('Failed to import members');
    }
  },

  // Fetch member transactions
  fetchMemberTransactions: async (memberId: string): Promise<MemberTransaction[]> => {
    try {
      const response = await axiosInstance.get<{ data: MemberTransaction[] }>(`${API_BASE_URL}/${memberId}/transactions`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching member transactions:', error);
      throw new Error('Failed to fetch member transactions');
    }
  },

  // Update transaction
  updateTransaction: async (data: UpdateTransactionRequest): Promise<MemberTransaction> => {
    try {
      const response = await axiosInstance.put<{ data: MemberTransaction }>(`${API_BASE_URL}/transactions/${data.id}`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error updating transaction:', error);
      throw new Error('Failed to update transaction');
    }
  },
};